import React, { useContext } from "react";
import CardApplicationAPI from "../../api/commercial/card-application";
import {
  ApplicationFlows,
  ApplicationFlowSteps,
} from "../../route/application/authenticated/application-flow/types";
import { ErrorContext } from "../../route/error/provider";
import { CardholderModel } from "../cardholder-basic-information/cardholder-form/types";
import CardholderBasicMapper from "../cardholder-basic-information/mapper";
import Signing from "./index";
import SigningMapper from "./mapper";
import { SignatoryModel } from "./types";
import { LanguageContext } from "../../context/languageContext";
import { ProductContext } from "../../context/product-context/productContext";
import { CardApplicationDocument } from "../../api/commercial/generated-types";

export default function SigningAdapter({
  flowType,
  cardApplication,
  triggerReload,
  setStep,
}: {
  flowType: ApplicationFlows;
  cardApplication: CardApplicationDocument;
  triggerReload: () => void;
  setStep: (step: ApplicationFlowSteps) => void;
}): React.JSX.Element {
  const { selectedProduct } = useContext(ProductContext);
  const hasMilesAndMore = selectedProduct?.hasFeature("MILES_AND_MORE");
  const basicAccountApplicationRef = cardApplication.basicAccountApplicationRef;
  const cardholderApplicationRef = cardApplication.applicationRef;
  const companyName = cardApplication.company.name;
  const ehraid = cardApplication.company.ehraid;

  const { language } = useContext(LanguageContext);

  const cardholder: CardholderModel =
    CardholderBasicMapper.mapCardholderDocumentToPageModel(
      cardApplication,
      language,
    );

  const signingModel: SignatoryModel =
    SigningMapper.mapDocumentToPageModel(cardApplication);

  const onNext = async (data: SignatoryModel): Promise<void> => {
    try {
      const requestSigningData = SigningMapper.mapPageModelToRequest(data);

      if (flowType === ApplicationFlows.ADDITIONAL_CA) {
        await CardApplicationAPI.saveAdditionalCardSignatoryData(
          requestSigningData,
          basicAccountApplicationRef,
          cardholderApplicationRef,
        );
        await CardApplicationAPI.readyForSigningAdditionalCardApplication(
          basicAccountApplicationRef,
          cardholderApplicationRef,
        );

        triggerReload();
        return;
      }

      await CardApplicationAPI.saveFirstCardSignatoryData(
        requestSigningData,
        basicAccountApplicationRef,
        cardholderApplicationRef,
      );

      await CardApplicationAPI.readyForSigningFirstCardApplication(
        basicAccountApplicationRef,
        cardholderApplicationRef,
      );
    } catch (e) {
      setError(e);
    }

    triggerReload();
  };

  const onPrevious = (): void => {
    const previousStep = hasMilesAndMore
      ? ApplicationFlowSteps.CARDHOLDER_BENEFITS
      : ApplicationFlowSteps.CARDHOLDER;
    setStep(previousStep);
  };

  const { setError } = useContext(ErrorContext);

  return (
    <Signing
      flowType={flowType}
      initial={signingModel}
      context={{
        selectedProduct: selectedProduct?.productRef,
        companyName,
        cardholder,
        ehraid,
      }}
      previous={onPrevious}
      next={onNext}
    />
  );
}
